import React, { createContext, useContext, useState, useEffect } from 'react';
import type { User } from '@shared/types';
import { getApiUrl } from '@/config';

interface AuthContextType {
    user: User | null;
    login: (username: string, password: string, latitude?: number, longitude?: number) => Promise<boolean>;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);

    // Migration logic: Support legacy 'authToken' by moving it to 'token'
    if (typeof window !== 'undefined' && !localStorage.getItem('token') && localStorage.getItem('authToken')) {
        const legacyToken = localStorage.getItem('authToken');
        if (legacyToken) {
            localStorage.setItem('token', legacyToken);
            localStorage.removeItem('authToken'); // Cleanup
            console.log('Migrated legacy auth token to new key.');
        }
    }

    useEffect(() => {
        // Check for stored session
        const storedUser = localStorage.getItem('currentUser');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    const login = async (username: string, password: string): Promise<boolean> => {
        let loginSuccess = false;

        try {
            const res = await fetch(getApiUrl('/api/login'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });

            if (res.ok) {
                const data = await res.json();
                if (data.success && data.user) {
                    setUser(data.user);
                    localStorage.setItem('currentUser', JSON.stringify(data.user));

                    // Store JWT token
                    if (data.token) {
                        localStorage.setItem('token', data.token);
                    }

                    loginSuccess = true;
                }
            }
        } catch (e) {
            console.error('Login error - using fallback authentication', e);

            // Fallback: Use hardcoded credentials when backend is unavailable
            const fallbackUsers = [
                { id: 'admin-1', username: 'admin', password: 'admin1234', role: 'admin' },
                { id: 'u1', username: 'laura', password: 'laura123', role: 'technician', technicianId: '1' },
                { id: 'u2', username: 'thomas', password: 'thomas123', role: 'technician', technicianId: '2' },
                { id: 'u3', username: 'marie', password: 'marie123', role: 'technician', technicianId: '3' },
                { id: 'u4', username: 'sophie', password: 'sophie123', role: 'technician', technicianId: '4' },
                { id: 'u5', username: 'julien', password: 'julien123', role: 'technician', technicianId: '5' },
                { id: 'u6', username: 'camille', password: 'camille123', role: 'technician', technicianId: '6' },
                { id: 'u7', username: 'marc', password: 'marc123', role: 'technician', technicianId: '7' },
                { id: 'u8', username: 'lea', password: 'lea123', role: 'technician', technicianId: '8' }
            ];

            const user = fallbackUsers.find(u =>
                u.username.toLowerCase() === username.toLowerCase() && u.password === password
            );

            if (user) {
                const { password: _, ...userWithoutPass } = user;
                setUser(userWithoutPass as User);
                localStorage.setItem('currentUser', JSON.stringify(userWithoutPass));
                loginSuccess = true;
            }
        }

        // Log the login attempt to localStorage
        logLoginAttempt(username, loginSuccess);

        return loginSuccess;
    };

    const logLoginAttempt = (username: string, success: boolean) => {
        try {
            const logs = JSON.parse(localStorage.getItem('loginLogs') || '[]');
            const newLog = {
                id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
                timestamp: new Date().toISOString(),
                username: username,
                status: success ? 'success' : 'failure'
            };

            logs.push(newLog);

            // Keep only last 100 logs
            if (logs.length > 100) {
                logs.splice(0, logs.length - 100);
            }

            localStorage.setItem('loginLogs', JSON.stringify(logs));
        } catch (e) {
            console.error('Error logging login attempt', e);
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('token'); // Remove JWT token
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
